import { useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { reviews } from "../../../../helpers/Arrays";
import "swiper/css";
import "swiper/css/pagination";
import "./Reviews.css";

const getInitials = (name) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const accentColors = ["#23aef4", "#c11a83"];

const ReviewCard = ({ item, index }) => {
  const accent = accentColors[index % accentColors.length];

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: { xs: "280px", md: "300px" },
        display: "flex",
        flexDirection: "column",
        p: { xs: 2.5, md: 3.5 },
        borderRadius: "20px",
        backgroundColor: "#fff",
        border: "1px solid rgba(89, 92, 97, 0.10)",
        boxShadow: "0 12px 36px rgba(10, 14, 24, 0.07)",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 22px 48px rgba(10, 14, 24, 0.12)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${accent} 0%, ${accent}88 100%)`,
        },
      }}
    >
      <FormatQuoteRoundedIcon
        sx={{
          fontSize: 40,
          color: accent,
          opacity: 0.35,
          mb: 1.5,
        }}
      />

      <Typography
        component="blockquote"
        sx={{
          flex: 1,
          m: 0,
          color: "primary.darker",
          fontSize: { xs: "15px", md: "16px" },
          lineHeight: 1.7,
          fontWeight: 400,
          fontStyle: "normal",
        }}
      >
        {item.review}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mt: 3,
          pt: 2.5,
          borderTop: "1px solid rgba(89, 92, 97, 0.08)",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${accent}22, ${accent}44)`,
            border: `2px solid ${accent}55`,
            color: accent,
            fontSize: "14px",
            fontWeight: 800,
          }}
        >
          {getInitials(item.name)}
        </Box>
        <Box>
          <Typography
            sx={{
              color: "primary.darker",
              fontSize: { xs: "15px", md: "16px" },
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            {item.name.trim()}
          </Typography>
          <Typography
            sx={{
              color: "primary.darker",
              fontSize: "12px",
              fontWeight: 500,
              opacity: 0.55,
              mt: 0.25,
            }}
          >
            Championship Participant
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Reviews = () => {
  const swiperRef = useRef(null);

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #fafbfc 0%, #ffffff 45%, #f7f9fc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(700px 400px at 92% 20%, rgba(35, 174, 244, 0.08), transparent 60%), radial-gradient(600px 380px at 8% 75%, rgba(193, 26, 131, 0.07), transparent 55%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1500px",
          margin: "0 auto",
          padding: { xs: "30px 20px", sm: "30px 60px", lg: "50px 120px" },
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "640px", margin: "0 auto" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.75,
              py: 0.75,
              mb: 2,
              borderRadius: "999px",
              backgroundColor: "rgba(193, 26, 131, 0.08)",
              border: "1px solid rgba(193, 26, 131, 0.18)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "12px", md: "13px" },
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#c11a83",
              }}
            >
              Testimonials
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "primary.darker",
              fontSize: { xs: "28px", sm: "34px", md: "42px" },
              lineHeight: 1.15,
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            What People Are{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Saying
            </Box>
          </Typography>

          <Box
            sx={{
              height: "4px",
              width: { xs: "80px", md: "200px" },
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
              margin: "16px auto 20px",
            }}
          />

          <Typography
            sx={{
              color: "primary.darker",
              lineHeight: 1.6,
              fontSize: { xs: "15px", md: "16px" },
              opacity: 0.85,
              mb: { xs: 4, md: 5 },
            }}
          >
            Hear from coaches, parents, and participants about their experience
            at the We Do Challenge championship.
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Swiper
            className="reviews-swiper"
            modules={[Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            loop
            loopAddBlankSlides
            loopAdditionalSlides={4}
            watchOverflow={false}
            slidesPerGroup={1}
            speed={600}
            autoplay={{
              delay: 5500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{ clickable: true }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            breakpoints={{
              700: {
                slidesPerView: 2,
                spaceBetween: 24,
                loopAdditionalSlides: 4,
              },
              1100: {
                slidesPerView: 2.5,
                spaceBetween: 28,
                loopAdditionalSlides: 4,
              },
            }}
          >
            {reviews.map((item, i) => (
              <SwiperSlide key={`${item.name}-${i}`}>
                <ReviewCard item={item} index={i} />
              </SwiperSlide>
            ))}
          </Swiper>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              gap: 1.5,
              mt: 3,
              position: "relative",
              zIndex: 5,
            }}
          >
            <IconButton
              type="button"
              aria-label="Previous review"
              onClick={() => swiperRef.current?.slidePrev()}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "#fff",
                border: "1px solid rgba(89, 92, 97, 0.14)",
                boxShadow: "0 8px 24px rgba(10, 14, 24, 0.08)",
                color: "primary.darker",
                "&:hover": {
                  backgroundColor: "rgba(193, 26, 131, 0.06)",
                  borderColor: "rgba(193, 26, 131, 0.3)",
                  color: "#c11a83",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              type="button"
              aria-label="Next review"
              onClick={() => swiperRef.current?.slideNext()}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "#fff",
                border: "1px solid rgba(89, 92, 97, 0.14)",
                boxShadow: "0 8px 24px rgba(10, 14, 24, 0.08)",
                color: "primary.darker",
                "&:hover": {
                  backgroundColor: "rgba(193, 26, 131, 0.06)",
                  borderColor: "rgba(193, 26, 131, 0.3)",
                  color: "#c11a83",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Reviews;
